const DEFAULT_BREAKPOINT = 900;
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function documentFor(root) {
  if (root?.nodeType === 9) return root;
  return root?.ownerDocument || globalThis.document;
}

function visibleFocusable(container) {
  return [...container.querySelectorAll(FOCUSABLE_SELECTOR)]
    .filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
}

export function mountResponsiveShell({
  root = globalThis.document,
  breakpoint = DEFAULT_BREAKPOINT,
} = {}) {
  const document = documentFor(root);
  const queryRoot = root?.querySelector ? root : document;
  const toggles = [...queryRoot.querySelectorAll('[data-mobile-nav-toggle]')];
  const toggle = toggles[0];
  const panel = queryRoot.querySelector('[data-mobile-nav-panel]');
  const backdrop = queryRoot.querySelector('[data-mobile-nav-backdrop]');
  if (!document || !toggle || !panel || !backdrop) return Object.freeze({ destroy() {}, close() {} });

  const media = globalThis.matchMedia(`(max-width: ${breakpoint}px)`);
  const observationRoot = document.body;
  const observer = new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1
      && (node.matches?.('.table-wrap') || node.querySelector?.('.table-wrap'))))) {
      enhanceResponsiveUi(document);
    }
  });
  let open = false;
  let destroyed = false;
  let returnFocusTarget = toggle;

  function setPanelAvailability(hidden) {
    panel.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    panel.inert = hidden;
  }

  function sync() {
    if (destroyed) return;
    const mobile = media.matches;
    if (!mobile) open = false;
    const concealed = mobile && !open;
    toggles.forEach((control) => {
      control.setAttribute('aria-expanded', mobile && open ? 'true' : 'false');
    });
    setPanelAvailability(concealed);
    panel.dataset.open = mobile && open ? 'true' : 'false';
    backdrop.hidden = !(mobile && open);
    document.body.classList.toggle('mobile-nav-open', mobile && open);
  }

  function close({ restoreFocus = true } = {}) {
    if (!media.matches || !open) return;
    open = false;
    sync();
    if (restoreFocus && returnFocusTarget?.isConnected) returnFocusTarget.focus();
  }

  function show() {
    if (!media.matches || open) return;
    open = true;
    sync();
    visibleFocusable(panel)[0]?.focus();
  }

  function onToggle(event) {
    returnFocusTarget = event.currentTarget;
    if (open) close();
    else show();
  }

  function onPanelClick(event) {
    if (event.target.closest?.('[data-mobile-nav-close]')) {
      close();
      return;
    }
    if (event.target.closest?.('a[href]')) close({ restoreFocus: false });
  }

  function onKeyDown(event) {
    if (!media.matches || !open) return;
    const activeModal = [...document.querySelectorAll('[aria-modal="true"]')]
      .find((modal) => !modal.closest('[hidden]'));
    if (activeModal && !panel.contains(activeModal)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = visibleFocusable(panel);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onMediaChange() {
    sync();
  }

  toggles.forEach((control) => control.addEventListener('click', onToggle));
  panel.addEventListener('click', onPanelClick);
  backdrop.addEventListener('click', close);
  document.addEventListener('keydown', onKeyDown);
  media.addEventListener('change', onMediaChange);
  observer.observe(observationRoot, { childList: true, subtree: true });
  sync();

  return Object.freeze({
    close,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      toggles.forEach((control) => control.removeEventListener('click', onToggle));
      panel.removeEventListener('click', onPanelClick);
      backdrop.removeEventListener('click', close);
      document.removeEventListener('keydown', onKeyDown);
      media.removeEventListener('change', onMediaChange);
      observer.disconnect();
      document.body.classList.remove('mobile-nav-open');
      panel.inert = false;
    },
  });
}

export function enhanceResponsiveUi(root = globalThis.document) {
  const queryRoot = root?.querySelectorAll ? root : globalThis.document;
  queryRoot.querySelectorAll('.table-wrap').forEach((wrap) => {
    if (!wrap.hasAttribute('tabindex')) wrap.tabIndex = 0;
    if (!wrap.hasAttribute('role')) wrap.setAttribute('role', 'region');
    if (!wrap.hasAttribute('aria-label') && !wrap.hasAttribute('aria-labelledby')) {
      const page = wrap.closest('.page') || queryRoot;
      const section = wrap.closest('section, article, [role="dialog"], .modal-card');
      const heading = section?.querySelector?.('h1, h2, h3') || page.querySelector?.('h1, h2');
      const name = heading?.textContent?.trim() || 'Records';
      wrap.setAttribute('aria-label', `${name} data table`);
    }
  });
}
